/** Resumable simulation work: the solver proof, the native verb proofs and
 * the determinism replay are each ONE generator that yields between ticks,
 * run to completion either synchronously (inside a worker thread, or a
 * shipped game's module load) or yielding to the event loop. A pass is
 * seconds to minutes of CPU; run synchronously on a loop that also serves
 * RPC or heartbeats, it starved vitest's 60 s worker RPC and a measured
 * build's lease heartbeat (#970). Both runners execute the identical work,
 * so their results are byte-identical. */

/** Ticks of simulation between yields (a fraction of a second of CPU), so
 * an async caller keeps its event loop live. */
export const PROOF_YIELD_TICKS = 30;

/** Run resumable work to completion synchronously: only where nothing else
 * shares the event loop (a worker thread body, a game module's load). */
export function drain<T>(work: Generator<void, T>): T {
  for (;;) { const next = work.next(); if (next.done) return next.value; }
}

/** Run resumable work to completion, yielding to the event loop at every
 * yield of the work. The yield is `setImmediate` (pending I/O runs first),
 * never a timer: a 0 ms timer waits at least 1 ms of wall clock, and the
 * CPU-bound work must not pace to the clock (a proof yields hundreds of
 * times per seed). */
export async function drainAsync<T>(work: Generator<void, T>): Promise<T> {
  for (;;) {
    const next = work.next();
    if (next.done) return next.value;
    await new Promise(resolve => setImmediate(resolve));
  }
}
